import { Box, Stack } from "@mui/material";
import { Common, Custom, FormResetPassword, Workflow } from "components";
import { useApp, useService, useTheme } from "contexts";
import { useWorkflow } from "contexts/components";
import { forwardRef, useImperativeHandle, useState } from "react";
import { Secrets } from "utils";
import { createStep } from "utils/helpers";

const StepSetup = forwardRef((_, ref) => {
  const { updateData, data } = useWorkflow<Workflow.Utils.OnboardingType>(); // Get updateData from context
  const { userService } = useService();
  const { theme } = useTheme();
  const { t } = useApp();

  const [isEmailInvalid, setIsEmailInvalid] = useState<boolean>(false);
  const [isPasswordValid, setIsPasswordValid] = useState<boolean>(false);

  const handlePasswordValid = (isValid: boolean) => {
    setIsPasswordValid(isValid);
  };

  const handleOnBlur = async (email: string) => {
    const response = await userService.isEmailInUse(email);
    setIsEmailInvalid(!!response);
  };

  useImperativeHandle(ref, () => ({
    validate: async () => {
      // Validate email
      if (isEmailInvalid) {
        return {
          isValid: false,
          message: t.message.validation.email_in_use,
        };
      }

      // validate password
      if (!isPasswordValid) {
        return {
          isValid: false,
          message: t.message.validation.password_policy,
        };
      }

      try {
        const response = await userService.insert({
          ...data.user,
          password: Secrets.hash(data.user.password),
          role: "user",
        });

        if (!response) {
          return {
            isValid: false,
            message: t.message.feedback.request_error,
          };
        }

        const updatedData = await updateData("user", response);

        return {
          isValid: true,
          data: updatedData,
          message: "",
        };
      } catch (error) {
        return {
          isValid: false,
          message: t.message.feedback.request_error,
        };
      }
    },
  }));

  return (
    <>
      <Custom.Typography size={theme.font.md} weight={theme.font.bold}>
        {t.component.workflow.onboarding.create_profile}
      </Custom.Typography>

      <Box mt={theme.spacing.md}>
        <Stack spacing={theme.spacing.sm}>
          <Custom.Typography size={theme.font.xs} weight={theme.font.bold}>
            {t.component.workflow.onboarding.setup_credentials}
          </Custom.Typography>
        </Stack>
      </Box>

      <Box mt={theme.spacing.md}>
        <Stack spacing={theme.spacing.sm}>
          <Custom.TextField
            required
            placeholder={t.placeholder.email_address}
            fullWidth
            value={data.user.email}
            error={isEmailInvalid}
            helperText={isEmailInvalid ? t.message.validation.email_in_use : ""}
            onBlur={(e) => handleOnBlur(e.target.value)}
            onChange={(e) => updateData("user.email", e.target.value)}
          />
        </Stack>
      </Box>

      <Box mt={theme.spacing.md}>
        <Stack spacing={theme.spacing.xs}>
          <FormResetPassword.Fields
            onPasswordChange={(password: string) =>
              updateData("user.password", password)
            }
            onPasswordValid={handlePasswordValid}
          />
        </Stack>
      </Box>

      <Box mt={theme.spacing.md}>
        <Stack spacing={theme.spacing.sm}>
          <Custom.Typography
            align="center"
            size={theme.font.sm}
            weight={theme.font.light}
          >
            {t.component.workflow.onboarding.terms_and_conditions}{" "}
            <Common.Link href="#">{t.label.terms_of_use}</Common.Link>{" "}
            {t.conjunction.and}{" "}
            <Common.Link href="#">{t.label.privacy_policy}</Common.Link>
          </Custom.Typography>
        </Stack>
      </Box>
    </>
  );
});

export default createStep<Workflow.Utils.OnboardingType>(StepSetup);
